import { Button } from "@/components/ui/Button";
import { QueryEntities } from "@/features/fast-dataset/components/QueryEntities";
import { useFastMoreRows } from "@/features/data-curation/hooks/useCuration";
import type { ExtractionDataset } from "@/features/data-curation/types";
import { Loader2, Plus } from "lucide-react";

const STAGES: Record<string, string> = {
  plan: "Planning the columns",
  search: "Searching the papers",
  screen: "Checking which passages hold the data",
  read: "Reading documents",
  fill: "Filling empty cells from the full text",
  done: "Done",
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** A Fast dataset's question with its entities, and one line of build status with More rows. */
export const FastQueryLine = ({ dataset }: { dataset: ExtractionDataset }) => {
  const run = dataset.builder;
  const more = useFastMoreRows(dataset.id);
  if (!run) return null;
  const running = run.status === "running" || more.isPending;
  const stage = run.stage ? (STAGES[run.stage] ?? run.stage) : null;
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2" aria-label="Build">
      <div className="min-w-0 max-w-3xl">
        <QueryEntities question={dataset.goal} entities={run.query_entities} />
      </div>
      <div
        className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-zinc-500"
        role="status"
        aria-live="polite"
      >
        {running && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" aria-hidden />}
        {running && <span className="text-zinc-700">{stage ?? "Starting"}…</span>}
        <span>
          <b className="font-semibold text-zinc-800">{run.rows_written}</b>{" "}
          {run.rows_written === 1 ? "row" : "rows"}
        </span>
        <span>{plural(run.papers_read, "paper")} read</span>
        {!running && run.status === "failed" && <span className="text-red-700">{run.error}</span>}
        {!running && run.exhausted && <span>No more papers for this question</span>}
        {dataset.status === "active" && !running && !run.exhausted && (
          <Button size="sm" variant="outline" onClick={() => more.mutate()} disabled={more.isPending}>
            <Plus className="h-4 w-4" aria-hidden />
            More rows
          </Button>
        )}
      </div>
    </div>
  );
};
